/**
 * What a person actually owns, so the filter sheet can grey out the rest.
 *
 * The All tab's filter chips fade the options you hold no cards for: no Fairy
 * cards means a visibly present but dim and unusable Fairy chip. That is better
 * than hiding it, because a child learns what exists and what they are missing,
 * which is most of the point of a collection app.
 *
 * TWO THINGS THIS FILE EXISTS TO GET RIGHT.
 *
 * First, the answer comes from the database, not from the array on screen. The
 * All tab loads in waves, so for the first second the in-memory list is a
 * fraction of the collection. Fading Fairy because the first wave happens not
 * to contain one would be a confident lie about what someone owns.
 *
 * Second, and this is the one that nearly shipped wrong: the facts are read
 * from the CATALOGUE, not from the collection row. `public.cards` does carry
 * `types` and `rarity` columns, but they are very nearly empty - measured
 * 09.11.2026, the largest collection in the product has 177 cards and ZERO of
 * them have either column filled in, and the next two have 13 of 133 and 6 of
 * 65. Built on those columns this would have faded almost every chip, which is
 * the same lie pointing the other way. Every collection card carries a
 * `card_id` that matches the catalogue exactly (177 of 177 for that
 * collection), and the catalogue knows the types, the rarity bucket and the
 * era. So the ids come from the collection and the facts come from the
 * catalogue.
 *
 * The map it returns is used for the FILTERING as well as the fading, so the
 * two can never disagree about what a card is.
 */
import { supabase } from '../lib/supabase';

/** PostgREST puts `in` lists in the URL, so ids go up in chunks. */
const ID_CHUNK = 100;

/**
 * @param {string} userId whose collection
 * @param {{ binderId?: string|null }} [options] scope to one binder
 * @returns {Promise<{
 *   byCard: Map<string, {types: string[], bucket: string|null, era: string|null}>,
 *   types: string[], rarities: string[], eras: string[], total: number
 * }|null>} null when the read failed, which is deliberately distinguishable
 *   from "you own nothing" - those two must not look the same on screen.
 */
export async function loadCollectionFacets(userId, { binderId = null } = {}) {
  if (!userId) return null;

  let q = supabase.from('cards').select('card_id').eq('user_id', userId);
  if (binderId) q = q.eq('binder_id', binderId);
  const { data: owned, error } = await q;
  if (error) {
    console.warn('[facets] collection read failed:', error.message);
    return null;
  }

  const ids = [...new Set((owned || []).map((r) => r.card_id).filter(Boolean))];
  if (ids.length === 0) {
    return { byCard: new Map(), types: [], rarities: [], eras: [], total: 0 };
  }

  const byCard = new Map();
  for (let i = 0; i < ids.length; i += ID_CHUNK) {
    const { data, error: cErr } = await supabase
      .schema('digitalcards')
      .from('catalog_browse')
      .select('id, types, rarity_bucket, era_id')
      .in('id', ids.slice(i, i + ID_CHUNK));
    if (cErr) {
      console.warn('[facets] catalogue read failed:', cErr.message);
      return null;
    }
    for (const row of data || []) {
      byCard.set(row.id, {
        types: row.types || [],
        bucket: row.rarity_bucket || null,
        era: row.era_id || null,
      });
    }
  }

  const types = new Set();
  const rarities = new Set();
  const eras = new Set();
  for (const v of byCard.values()) {
    for (const t of v.types) if (t) types.add(t);
    if (v.bucket) rarities.add(v.bucket);
    if (v.era) eras.add(v.era);
  }

  return {
    byCard,
    types: [...types],
    rarities: [...rarities],
    eras: [...eras],
    total: ids.length,
  };
}
